import "./App.css";
import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ThemeProvider from "./layout/provider/Theme";
import { RootState, AppDispatch } from "./store/store";
import { fetchCurrentUser } from "./store/slices/misc/currentUser";

// Layouts
import Layout from "./layout/Index";
import LayoutNoSidebar from "./layout/Index-nosidebar";

// Auth Pages
import Login from "./pages/auth/Login";
import ResetPassword from "./pages/auth/ResetPassword";

// Main Pages — lazy-loaded for code splitting
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const ProjectsList = lazy(() => import("./pages/projects/ProjectsList"));
const ProjectDetail = lazy(() => import("./pages/projects/ProjectDetail"));
const AccountPage = lazy(() => import("./pages/account/AccountPage"));

// Master Pages — lazy-loaded for code splitting
const RateLibrary = lazy(() => import("./pages/masters/RateLibrary"));
const Materials = lazy(() => import("./pages/masters/Materials"));
const UOM = lazy(() => import("./pages/masters/UOM"));
const IFCClassifications = lazy(() => import("./pages/masters/IFCClassifications"));
const BOQTemplates = lazy(() => import("./pages/masters/BoqTemplates"));
const QTORules = lazy(() => import("./pages/masters/QTORules"));
const AutoLinkRules = lazy(() => import("./pages/masters/AutoLinkRules"));
const AiConfiguration = lazy(() => import("./pages/masters/AiConfiguration"));
const UserAccess = lazy(() => import("./pages/masters/UserAccess"));
const AuditLog = lazy(() => import("./pages/masters/AuditLog"));
// 2D (DWG) masters + standalone 2D viewer
const MeasurementStandards = lazy(() => import("./pages/masters/MeasurementStandards"));
const LayerClassification = lazy(() => import("./pages/masters/LayerClassification"));
const AssumedSections = lazy(() => import("./pages/masters/AssumedSections"));
const SectionProfiles = lazy(() => import("./pages/masters/SectionProfiles"));
const DerivedRules = lazy(() => import("./pages/masters/DerivedRules"));
// 3D / Global Masters pages (from feat/global-masters)
const GlobalMasters = lazy(() => import("./pages/masters/GlobalMasters"));
const ProjectAtlas = lazy(() => import("./pages/ProjectAtlas"));
const ResourceCatalog = lazy(() => import("./pages/masters/ResourceCatalog"));
const Assemblies = lazy(() => import("./pages/masters/Assemblies"));
const ExchangeRates = lazy(() => import("./pages/masters/ExchangeRates"));
// 2D flow uses the APS-based DWG viewer page (global-masters' TwoDViewerPage.tsx was never committed).
const TwoDViewerPage = lazy(() => import("./pages/projects/ApsDwgViewerPage"));
// full-screen AI workspace of a project (drawings | chat | conversations / drawing)
const AiWorkspace = lazy(() => import("./pages/projects/agents/workspace/AiWorkspace"));
const NewProject = lazy(() => import("./pages/projects/NewProject"));

// Viewer — lazy-loaded (heaviest page: Three.js + web-ifc)
const ViewerPage = lazy(() => import("./pages/viewer/ViewerPage"));
const SharedViewPage = lazy(() => import("./pages/viewer/SharedViewPage"));

const PageLoader = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: "#8094ae" }}>
    Loading...
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.currentUser.isAuthenticated
  );
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const App = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector(
    (state: RootState) => state.currentUser.isAuthenticated
  );

  // On app boot, if we have a stored token, decode user info
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, isAuthenticated]);

  return (
    <ThemeProvider>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Full-page 3D Viewer — outside Layout */}
        <Route
          path="/projects/:projectId/viewer/:modelId"
          element={
            <ProtectedRoute>
              <ViewerPage />
            </ProtectedRoute>
          }
        />

        {/* Auth Routes - No Sidebar */}
        <Route element={<LayoutNoSidebar />}>
          <Route
            path="/"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
            }
          />
          {/* Password-reset landing (from the emailed link) */}
          <Route path="/auth/verifyToken" element={<ResetPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Protected Routes - With Sidebar */}
        <Route
          element={
            <ProtectedRoute>
              <Layout title="ProcureX BIM" />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/atlas" element={<ProjectAtlas />} />
          <Route path="/projects" element={<ProjectsList />} />
          <Route path="/projects/new" element={<NewProject />} />
          <Route path="/projects/:projectId" element={<ProjectDetail />} />
          <Route path="/projects/:projectId/:tab" element={<ProjectDetail />} />

          {/* Masters */}
          <Route path="/masters/global" element={<GlobalMasters />} />
          <Route path="/masters/resources" element={<ResourceCatalog />} />
          <Route path="/masters/assemblies" element={<Assemblies />} />
          <Route path="/masters/exchange-rates" element={<ExchangeRates />} />
          <Route path="/masters/rate-library" element={<RateLibrary />} />
          <Route path="/masters/materials" element={<Materials />} />
          <Route path="/masters/uom" element={<UOM />} />
          <Route path="/masters/ifc-classifications" element={<IFCClassifications />} />
          <Route path="/masters/boq-templates" element={<BOQTemplates />} />
          <Route path="/masters/qto-rules" element={<QTORules />} />
          <Route path="/masters/auto-link-rules" element={<AutoLinkRules />} />
          <Route path="/masters/measurement-standards" element={<MeasurementStandards />} />
          <Route path="/masters/layer-classification" element={<LayerClassification />} />
          <Route path="/masters/assumed-sections" element={<AssumedSections />} />
          <Route path="/masters/section-profiles" element={<SectionProfiles />} />
          <Route path="/masters/derived-rules" element={<DerivedRules />} />
          <Route path="/projects/:projectId/viewer-2d/:dwgId" element={<TwoDViewerPage />} />
          <Route path="/projects/:projectId/ai" element={<AiWorkspace />} />
          <Route path="/masters/ai-configuration" element={<AiConfiguration />} />
          <Route path="/masters/user-access" element={<UserAccess />} />
          <Route path="/masters/audit-log" element={<AuditLog />} />

          {/* My account (profile menu) */}
          <Route path="/account" element={<Navigate to="/account/profile" replace />} />
          <Route path="/account/:tab" element={<AccountPage />} />
        </Route>

        {/* Public Shared View Route (no auth required) */}
        <Route path="/share/v/:token" element={<SharedViewPage />} />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </ThemeProvider>
  );
};

export default App;
